import type {
  AmountsByMethodId,
  WithdrawalProfitAmounts,
} from "@/pages/withdrawals/types";
import Decimal from "decimal.js";
import { useCallback, useEffect, useState } from "react";

export type MethodConfig = { id: string; max?: Decimal; ledgerId?: string };

const calcRemainingAmount = (
  initialValue: Decimal,
  amounts: (Decimal | undefined)[]
) => {
  return initialValue.sub(
    amounts.reduce<Decimal>(
      (acc, value) => acc.add(value ?? new Decimal(0)),
      new Decimal(0)
    )
  );
};

const calcMaximumLimitForMethod = (
  method: MethodConfig,
  initialAmount: Decimal,
  amounts: AmountsByMethodId
) => {
  const remainingAmount = calcRemainingAmount(
    initialAmount,
    Object.values(amounts)
  ).add(amounts[method.id] ?? new Decimal(0));

  return method.max === undefined || method.max.isZero()
    ? remainingAmount
    : remainingAmount.lt(method.max)
      ? remainingAmount
      : method.max;
};

export const useWithdrawalAmounts = (
  initialProfitAmount: Decimal,
  onChange?: (changes: WithdrawalProfitAmounts) => void
) => {
  const initialProfitAmountValue = initialProfitAmount.toString();

  const [profitAmountLeft, setProfitAmountLeft] =
    useState<Decimal>(initialProfitAmount);
  const [profitAmounts, setProfitAmounts] = useState<AmountsByMethodId>({});
  const [newProfitPaymentMethodId, setNewProfitPaymentMethodId] =
    useState<string>("");

  const setProfitAmount = useCallback(
    (methodId: string, amount: Decimal | undefined, clearOthers = false) => {
      if (clearOthers) {
        setProfitAmounts({ [methodId]: amount });
      } else {
        setProfitAmounts((prev) => ({ ...prev, [methodId]: amount }));
      }
    },
    []
  );

  const clearProfitAmounts = useCallback(() => {
    setProfitAmounts({});
  }, []);

  useEffect(() => {
    setProfitAmountLeft(
      calcRemainingAmount(initialProfitAmount, Object.values(profitAmounts))
    );
  }, [profitAmounts, initialProfitAmountValue]);

  useEffect(() => {
    //bad workaround for something worse than this. TODO, refactor the logic of the withdrawal flow. Suggestion: useReducer.
    // const handler = ()=>{
    onChange?.({
      profitAmountLeft,
      profitAmounts,
      newProfitPaymentMethodId,
    });
    // }
    // document.addEventListener('WithdrawAmountInputChange', handler);

    // return () => document.removeEventListener('WithdrawAmountInputChange', handler);
  }, [profitAmountLeft, profitAmounts, newProfitPaymentMethodId, onChange]);

  const getMaximumProfitAmount = useCallback(
    (method: MethodConfig) =>
      calcMaximumLimitForMethod(method, initialProfitAmount, profitAmounts),
    [profitAmounts, initialProfitAmountValue]
  );

  return {
    setProfitAmount,
    clearProfitAmounts,
    profitAmountLeft,
    profitAmounts,
    newProfitPaymentMethodId,
    setNewProfitPaymentMethodId,
    getMaximumProfitAmount,
  };
};
